import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

export default function AddAddress() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get('return_to');
    const giftId = searchParams.get('gift_id');

    const [form, setForm] = useState({
        name: '',
        phone: '',
        region: '',
        address: '',
        is_default: false,
        tag: '',
    });
    const [smartText, setSmartText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const tags = ['家', '公司', '学校', '父母', '朋友', '自定义'];

    const handleSmartParse = () => {
        if (!smartText.trim()) return;
        // Basic parser for Chinese addresses: finds an 11-digit phone, assumes the rest is address/name
        let text = smartText;
        const phoneMatch = text.match(/1[3-9]\d{9}/);
        let parsedPhone = '';
        if (phoneMatch) {
            parsedPhone = phoneMatch[0];
            text = text.replace(parsedPhone, '').trim();
        }

        // Very rough heuristic: first word might be name, rest is address
        const parts = text.split(/[\s,，]+/).filter(Boolean);
        let parsedName = '';
        let parsedAddress = text;

        if (parts.length > 1) {
            // Assume shortest part is name
            const sortedParts = [...parts].sort((a, b) => a.length - b.length);
            if (sortedParts[0].length <= 4) {
                parsedName = sortedParts[0];
                parsedAddress = text.replace(parsedName, '').trim();
            }
        }

        setForm(prev => ({
            ...prev,
            name: parsedPhone && !parsedName ? prev.name : parsedName || prev.name,
            phone: parsedPhone || prev.phone,
            address: parsedAddress || prev.address,
        }));

        toast.success('识别成功，请核对信息');
    };

    const handleSave = async () => {
        if (!form.name.trim()) return toast.error('请填写收件人');
        if (!form.phone.trim()) return toast.error('请填写手机号');
        if (!/^\d{11}$/.test(form.phone.trim())) return toast.error('手机号必须是11位数字');
        if (!form.region.trim()) return toast.error('请填写所在地区');
        if (!form.address.trim()) return toast.error('请填写详细地址');

        // Split region roughly into province city district
        const rParts = form.region.split(/\s+/);
        const province = rParts[0] || '';
        const city = rParts[1] || rParts[0] || '';
        const district = rParts[2] || '';

        try {
            setSubmitting(true);
            const { data } = await api.post('/addresses', {
                name: form.name,
                phone: form.phone,
                province,
                city,
                district,
                address: form.address,
                is_default: form.is_default,
            });

            const addressId = data?.address?.id;

            // If started from a gift, auto-bind the delivery info
            if (giftId && addressId) {
                try {
                    await api.post(`/reward-gifts/${giftId}/delivery-info`, {
                        name: form.name,
                        phone: form.phone,
                        address: `${form.region} ${form.address}`,
                    });
                    toast.success('收货信息已提交');
                } catch (e) {
                    // Ignore gift bind error, just continue
                }
            } else {
                toast.success('地址保存成功');
            }

            if (returnTo) {
                navigate(returnTo);
            } else {
                navigate(-1);
            }
        } catch (e: any) {
            toast.error(e?.response?.data?.message || '保存失败');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-[100vh] bg-[#F7F8FA] pb-24">
            <AuthGuard />

            {/* Header */}
            <div className="bg-white px-4 py-3 flex items-center sticky top-0 z-10">
                <button
                    onClick={() => navigate(-1)}
                    className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-black"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                </button>
                <h1 className="flex-1 text-center text-[17px] font-medium mr-6">添加收货地址</h1>
            </div>

            <div className="px-3 pt-3">
                {/* Banner */}
                <div className="bg-[#FFF4E6] rounded-t-2xl rounded-b-lg p-3 flex items-center gap-2 mb-3">
                    <div className="text-xl">🎃</div>
                    <div className="text-[#D97706] text-[13px] font-medium">请填写收货地址用于商品配送</div>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-xl p-4 mb-3">
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-[15px]">地址信息</span>
                        <label className="flex items-center gap-1.5 text-[13px] text-gray-500">
                            <input
                                type="radio"
                                checked={form.is_default}
                                onClick={() => setForm({ ...form, is_default: !form.is_default })}
                                readOnly
                                className="w-4 h-4 accent-[#F97316]"
                            />
                            默认收货地址
                        </label>
                    </div>

                    <div className="space-y-0 relative">
                        {/* Name */}
                        <div className="flex items-center py-3.5 border-b border-gray-100">
                            <span className="w-20 text-[14px] text-gray-700">收件人<span className="text-red-500">*</span></span>
                            <input
                                type="text"
                                placeholder="收件人名字"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                className="flex-1 text-[14px] bg-transparent outline-none placeholder-gray-300"
                            />
                        </div>

                        {/* Phone */}
                        <div className="flex items-center py-3.5 border-b border-gray-100">
                            <span className="w-20 text-[14px] text-gray-700">手机号<span className="text-red-500">*</span></span>
                            <div className="flex items-center gap-1 text-[14px] mr-2">
                                +86 <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                            <input
                                type="tel"
                                placeholder="手机号"
                                value={form.phone}
                                onChange={e => setForm({ ...form, phone: e.target.value })}
                                className="flex-1 text-[14px] bg-transparent outline-none placeholder-gray-300"
                            />
                        </div>

                        {/* Region */}
                        <div className="flex items-center py-3.5 border-b border-gray-100">
                            <span className="w-20 text-[14px] text-gray-700 whitespace-nowrap">所在地区<span className="text-red-500">*</span></span>
                            <input
                                type="text"
                                placeholder="省、市、区、街道"
                                value={form.region}
                                onChange={e => setForm({ ...form, region: e.target.value })}
                                className="flex-1 text-[14px] bg-transparent outline-none placeholder-gray-300 pr-6"
                            />
                            <svg className="w-4 h-4 text-[#F97316] absolute right-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                        </div>

                        {/* Detail Address */}
                        <div className="flex items-start py-3.5 border-b border-gray-100 min-h-[80px]">
                            <span className="w-20 text-[14px] text-gray-700">详细地址<span className="text-red-500">*</span></span>
                            <textarea
                                placeholder="小区、写字楼、门牌号等"
                                value={form.address}
                                onChange={e => setForm({ ...form, address: e.target.value })}
                                className="flex-1 text-[14px] bg-transparent outline-none placeholder-gray-300 resize-none h-12 pr-6 leading-relaxed"
                            />
                            <svg className="w-4 h-4 text-[#F97316] absolute right-0 mt-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                            </svg>
                        </div>

                        {/* Tags */}
                        <div className="flex items-center py-3.5 pt-4">
                            <span className="w-20 text-[14px] text-gray-700 shrink-0">地址标签</span>
                            <div className="flex flex-wrap gap-2">
                                {tags.map(t => (
                                    <span
                                        key={t}
                                        onClick={() => setForm({ ...form, tag: t })}
                                        className={`px-3 py-1 rounded-full text-[12px] transition ${form.tag === t ? 'bg-[#F97316] text-white' : 'bg-[#FFF3E8] text-[#D97706]'}`}
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Smart Fill Card */}
                <div className="bg-white rounded-xl p-4">
                    <span className="font-bold text-[15px] block mb-2">智能填写</span>
                    <div className="relative">
                        <textarea
                            value={smartText}
                            onChange={e => setSmartText(e.target.value)}
                            placeholder="粘贴收货信息到此处，将自动识别姓名/电话/地址"
                            className="w-full bg-gray-50 rounded-lg p-3 text-[13px] outline-none placeholder-gray-400 min-h-[80px] pb-8 resize-none"
                        />
                        <button
                            onClick={handleSmartParse}
                            className="absolute bottom-2 right-3 text-[#F97316] text-[13px] font-medium"
                        >
                            粘贴并识别
                        </button>
                    </div>
                </div>
            </div>

            {/* Bottom Sticky Action Area */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 pb-safe">
                <button
                    onClick={handleSave}
                    disabled={submitting}
                    className="w-full bg-[#F97316] text-white rounded-full py-3 text-[15px] font-medium disabled:opacity-60 active:opacity-80 transition"
                >
                    {submitting ? '保存中...' : '保存'}
                </button>
            </div>
        </div>
    );
}
